import { Map as ImmutableMap } from 'immutable';
import { isEmpty } from 'lodash';

import { BetsState, RecordOfStakeEntity, StakesState } from '../../stakes/types';

import {
  getBalancingFunctionByStakeType,
  getLineStakeTypeGroupConf,
  isFilteredLineStake,
} from 'app/lib/matchGridStakes';

// ---

type LineId = number;
type GroupId = number;
type StakeId = number;
type MatchStakesByType = Record<string, ImmutableMap<StakeId, RecordOfStakeEntity>>;

export type LineEntryResult = ImmutableMap<GroupId, StakeId>;
export type MatchEntryResult = ImmutableMap<LineId, LineEntryResult>;

type LineStakeTypeGroup = {
  groupId: number;
  stakeType: number;
  stakes: ImmutableMap<StakeId, RecordOfStakeEntity>;
};

export default function getMatchLines(
  sportId: number | undefined,
  stakes: StakesState,
  bets: BetsState,
): MatchEntryResult {
  if (typeof sportId !== 'number') {
    return ImmutableMap(ImmutableMap());
  }

  const sportLinesStakeType = getLineStakeTypeGroupConf(sportId);

  const matchStakesByType = stakes.reduce((map, stake) => {
    if (!stake?.type) {
      return map;
    }

    map[stake.type] = (map[stake.type] || ImmutableMap()).set(stake.id, stake);

    return map;
  }, {} as MatchStakesByType);

  const lineStakeTypeGroup: Record<string, LineStakeTypeGroup[]> = {};

  sportLinesStakeType.forEach(({ lineId, groupId, stakeType, filter }) => {
    if (stakeType in matchStakesByType) {
      let filteredStakes;

      // Заметка: Зачем нужен фильтр isFilteredLineStake
      // У разных линий может быть stakeType с одинаковым id, но с разным полем period. Нужно взять stake.params и посмотреть
      // Если stake.params.period === filter.period, тогда эта ставка нам подходит
      // global.LINE_STAKE_TYPE_CONF = {
      //   '1': [
      //     {
      //       'lineId': 21,
      //       'stakeTypes': [{ 'stakeType': 206372, 'filter': { 'period': 1.0 } }]
      //     }, {
      //       'lineId': 22,
      //       'stakeTypes': [{ 'stakeType': 206372, 'filter': { 'period': 2.0 } }]
      //     }
      //   ]
      // };
      if (filter && !isEmpty(filter)) {
        filteredStakes = matchStakesByType[stakeType].filter((stake) => (
          isFilteredLineStake(filter, stake.params)
        ));
      }
      else {
        filteredStakes = matchStakesByType[stakeType];
      }

      if (filteredStakes.size > 0) {
        if (lineId in lineStakeTypeGroup) {
          lineStakeTypeGroup[lineId].push({ groupId, stakeType, stakes: filteredStakes });
        }
        else {
          lineStakeTypeGroup[lineId] = [{ groupId, stakeType, stakes: filteredStakes }];
        }
      }
    }
  });

  const matchLinesResult = Object.keys(lineStakeTypeGroup).reduce((matchEntryResult, key) => {
    const lineId = Number(key);

    const result = lineStakeTypeGroup[key].reduce((lineEntryResult, info) => {
      // Нужно найти самую сбалансирванную ставку. То есть, ставку шанс выигрыша которой 50 на 50
      // Получаем функцию, которая сбалансирует наши ставки (balancingFunction)
      const balancingFunction = getBalancingFunctionByStakeType(info.stakeType);
      const balancedStakeId = balancingFunction(info.stakes, bets);

      if (balancedStakeId) {
        return lineEntryResult.set(info.groupId, balancedStakeId);
      }

      return lineEntryResult;
    }, (ImmutableMap() as LineEntryResult).asMutable());

    return matchEntryResult.set(lineId, result.asImmutable());
  }, (ImmutableMap() as MatchEntryResult).asMutable());

  return matchLinesResult.asImmutable();
}
